// 「警戒」卡（2026-08-19 業主裁決：五視角評審根因 R5 的保守收斂案 §3.B）。
//
// 收斂前：家頁有三個並列的軸回答同一個問題「什麼時候要警戒」——安全模式 / 一鍵場景 /
// 依位置自動警戒，等於把系統內部模型直接當成使用者的資訊架構。收斂後併成**一張卡**：
//   模式選擇（在家／外出）在上 → 回推提示 → 快捷（原「一鍵場景」）→ 依位置自動警戒在最下。
//
// **寫入路徑不動**（業主裁決 ①）：仍是逐台 op:set 的本機批次（planArm + applyPlan），
// 不改走雲端 scene。理由：現在離線也切得動，改了就必須連線，而使用者最想切佈防的時候
// 常常正是網路有問題的時候。
//
// 新增的是**回推**（評審裁決 C1，見 armingDrift.ts 的完整理由）：進頁時讀各機實際的
// MOTION/OBJECT Enabled，與本機模式標籤不符時在卡上明講並給一顆「重新套用」。
// 「重新套用」**不是新的寫入入口** —— 它走的就是模式切換那一條 applyPlan。
//
//   - 模式存 client 端偏好（homeAwayMode store，localStorage）；登出清空。
//   - 切換＝樂觀先記偏好，再對「可控且在線、具偵測能力」的攝影機批次 op:set Motion/Object。
//   - 逐台 best-effort：離線/無權限的裝置略過並在摘要標明；個別失敗不擋其餘。

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { signaling } from '../../lib/ws';
import type { DeviceSummary } from '../devices/deviceModel';
import { frameValue, opGet, opSet } from '../devices/settings/ops';
import { ScenesPanel } from '../scenes/ScenesPanel';
import { normalizeBool, summarizeDrift, type DriftReport } from './armingDrift';
import { planArm, type ArmPlan } from './homeAwayApply';
import { getMode, setMode, subscribeMode, type HomeAwayMode } from './homeAwayMode';
import { GeofencePresenceControl } from './GeofencePresenceControl';

interface ApplyOutcome {
  mode: HomeAwayMode;
  /** 成功套用的裝置台數。 */
  armed: number;
  /** op:set 失敗（逾時 / 拒絕）的裝置台數。 */
  failed: number;
  /** 具偵測能力但離線而略過。 */
  offline: number;
  /** 具偵測能力但無控制權而略過。 */
  noPermission: number;
  /** 計畫中的目標台數（armed + failed）。 */
  total: number;
}

/** 逐台 op:set 佈防；best-effort，回報成敗計數。side-effect 集中在此，plan 為純輸入。 */
async function applyPlan(plan: ArmPlan): Promise<ApplyOutcome> {
  signaling.connect();
  let armed = 0;
  let failed = 0;
  for (const target of plan.targets) {
    try {
      // 加入 room（冪等 + refcount，與 useDeviceList/ensureEventRooms 共存不互踩）。
      await signaling.subscribe(target.deviceId).catch(() => undefined);
      for (const op of target.ops) {
        await opSet(target.deviceId, op.id, op.value);
      }
      armed += 1;
    } catch {
      failed += 1;
    } finally {
      void signaling.unsubscribe(target.deviceId).catch(() => undefined);
    }
  }
  return {
    mode: plan.mode,
    armed,
    failed,
    offline: plan.skipped.offline,
    noPermission: plan.skipped.noPermission,
    total: plan.targets.length,
  };
}

/**
 * 回推：逐台 op:get 計畫中的 attr，彙整成 DriftReport（比對邏輯在 armingDrift.ts，純函式）。
 * 只讀不寫。單台/單 attr 失敗吞掉並記為未知，不讓一台逾時毀掉整輪檢查。
 */
async function probeArmState(plan: ArmPlan): Promise<DriftReport> {
  signaling.connect();
  const readings = new Map<string, Map<string, boolean | undefined>>();
  for (const target of plan.targets) {
    const perAttr = new Map<string, boolean | undefined>();
    try {
      await signaling.subscribe(target.deviceId).catch(() => undefined);
      for (const op of target.ops) {
        try {
          perAttr.set(op.id, normalizeBool(frameValue(await opGet(target.deviceId, op.id))));
        } catch {
          perAttr.set(op.id, undefined); // 未知，不算不符
        }
      }
    } finally {
      void signaling.unsubscribe(target.deviceId).catch(() => undefined);
    }
    readings.set(target.deviceId, perAttr);
  }
  return summarizeDrift(plan, readings);
}

export function HomeAwayControl({ devices }: { devices: DeviceSummary[] | null }) {
  const { t } = useTranslation();
  // getMode 回傳值在模式改變時 identity 即變，useSyncExternalStore 以 Object.is 比對即重繪。
  const mode = useSyncExternalStore(subscribeMode, getMode, getMode);

  const [applying, setApplying] = useState<HomeAwayMode | null>(null);
  const [outcome, setOutcome] = useState<ApplyOutcome | null>(null);
  const [drift, setDrift] = useState<DriftReport | null>(null);
  const alive = useRef(true);
  // APP#61：同步 in-flight guard。`applying` 為 async state，同步窗口連點兩次都讀到舊值 → 並發相反
  // apply 競態。以 ref 於進入 applyMode 當下立刻同步設旗標、早退重入，關閉這個窗口。
  const applyingRef = useRef(false);
  // devices 由輪詢而來、identity 每輪都換；回推 effect 只能依「裝置集合 + 模式」觸發，
  // 否則每次輪詢都會多打一輪 op:get。實際讀值時再從 ref 取最新清單。
  const devicesRef = useRef(devices);
  devicesRef.current = devices;
  // 排序後再組 key：清單順序若因輪詢而變動，未排序的 key 會變、白白多打一輪 op:get。
  const deviceKey = useMemo(() => (devices ?? []).map((d) => d.device_id).sort().join(','), [devices]);
  // 回推是非同步的；晚回來的舊輪次不得覆寫新輪次的結果。
  const probeSeq = useRef(0);
  // ⚠️ socket 未開時 sendCmd 會**立刻**以 'signaling not connected' reject（ws.ts
  // sendCorrelated）。家頁掛載當下 socket 通常還在 connecting，若不等 'open' 就打，整輪
  // 讀值會全部變成「未知」→ 提示永遠不出現，而且是靜默的（測試用替身打不到這一層）。
  const [wsOpen, setWsOpen] = useState(() => signaling.getState() === 'open');

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    signaling.connect();
    setWsOpen(signaling.getState() === 'open');
    return signaling.on('state', (s) => setWsOpen(s === 'open'));
  }, []);

  /** 回推一輪（唯讀）。套用中不跑——那時裝置正在被寫，讀到的是過渡值。 */
  const checkDrift = useCallback(async () => {
    if (applyingRef.current) return;
    const plan = planArm(devicesRef.current ?? [], getMode());
    const seq = ++probeSeq.current;
    if (plan.targets.length === 0) {
      setDrift(null);
      return;
    }
    try {
      const report = await probeArmState(plan);
      if (alive.current && seq === probeSeq.current) setDrift(report);
    } catch {
      if (alive.current && seq === probeSeq.current) setDrift(null);
    }
  }, []);

  useEffect(() => {
    if (!wsOpen) return; // 等 socket 開了再讀；重連後會再跑一輪
    void checkDrift();
  }, [deviceKey, mode, wsOpen, checkDrift]);

  /**
   * 套用模式到裝置。`force` ＝「重新套用」（模式沒變也要重打一次，用來修掉回推出來的不符）。
   */
  const applyMode = useCallback(
    async (next: HomeAwayMode, force = false) => {
      if ((!force && next === getMode()) || applyingRef.current) return;
      applyingRef.current = true; // 同步佔位，先於任何 await / 早退
      try {
        setOutcome(null);
        setDrift(null); // 舊的不符報告在重新套用後即失效
        setMode(next); // 樂觀：先記偏好（persist + notify）；force 時為 no-op
        const plan = planArm(devicesRef.current ?? [], next);
        if (plan.targets.length === 0) {
          // 沒有可套用的裝置：模式已記住，僅提示無裝置可套用。
          setOutcome({ mode: next, armed: 0, failed: 0, offline: plan.skipped.offline, noPermission: plan.skipped.noPermission, total: 0 });
          return;
        }
        setApplying(next);
        try {
          const res = await applyPlan(plan);
          if (alive.current) setOutcome(res);
        } finally {
          if (alive.current) setApplying(null);
        }
      } finally {
        applyingRef.current = false;
        void checkDrift(); // 套用完再回推一次，確認實況真的跟上了
      }
    },
    [checkDrift]
  );

  const isArmed = mode === 'away';
  const busy = applying !== null;
  const mismatched = drift?.mismatched.length ?? 0;

  return (
    <section className="home-axis home-axis-mode" aria-labelledby="home-mode-h">
      <header className="home-axis-head">
        <h2 id="home-mode-h">{t('homeAway.title')}</h2>
      </header>
      <div className="card home-mode-card">
        <div className="home-mode-segment" role="group" aria-label={t('homeAway.title')}>
          <button
            type="button"
            className={`home-mode-seg ${!isArmed ? 'home-mode-seg-active' : ''}`}
            aria-pressed={!isArmed}
            disabled={busy}
            onClick={() => void applyMode('home')}
          >
            <span className="home-mode-seg-icon" aria-hidden>
              ⌂
            </span>
            {t('homeAway.home')}
          </button>
          <button
            type="button"
            className={`home-mode-seg ${isArmed ? 'home-mode-seg-active' : ''}`}
            aria-pressed={isArmed}
            disabled={busy}
            onClick={() => void applyMode('away')}
          >
            <span className="home-mode-seg-icon" aria-hidden>
              →
            </span>
            {t('homeAway.away')}
          </button>
        </div>

        <p className="text-secondary home-mode-desc">{isArmed ? t('homeAway.awayDesc') : t('homeAway.homeDesc')}</p>

        <p className="home-mode-status" role="status" aria-live="polite">
          {busy ? t('homeAway.applying') : outcome ? summaryText(outcome, t) : ''}
        </p>

        {/* 回推提示（C1）：本機標籤與裝置實況不符時才出現。 */}
        {mismatched > 0 && (
          <div className="home-mode-drift" role="status" data-testid="arming-drift">
            <p className="home-mode-drift-text">
              {t('homeAway.driftNotice', { count: mismatched, mode: isArmed ? t('homeAway.away') : t('homeAway.home') })}
            </p>
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => void applyMode(mode, true)}>
              {t('homeAway.reapply')}
            </button>
          </div>
        )}

        <ScenesPanel />
        <GeofencePresenceControl devices={devices ?? []} disabled={busy} />
      </div>
    </section>
  );
}

/** 依套用結果組人話摘要（純函式，好測）。 */
export function summaryText(o: ApplyOutcome, t: (k: string, opts?: Record<string, unknown>) => string): string {
  if (o.total === 0 && o.offline === 0 && o.noPermission === 0) {
    return t('homeAway.noDetectionDevices');
  }
  const parts: string[] = [];
  if (o.armed > 0) {
    parts.push(o.mode === 'away' ? t('homeAway.doneArmed', { count: o.armed }) : t('homeAway.doneRelaxed', { count: o.armed }));
  }
  if (o.failed > 0) parts.push(t('homeAway.someFailed', { count: o.failed }));
  if (o.offline > 0) parts.push(t('homeAway.skippedOffline', { count: o.offline }));
  if (o.noPermission > 0) parts.push(t('homeAway.skippedNoPermission', { count: o.noPermission }));
  return parts.join(' · ');
}
